import { SpartenType } from '@ps20/bff-models';
import {
  App,
  FeatureToggles,
  FileUploader,
  UnderwriterMeasure,
  UploadedFile,
} from '@ps20-frontend/frontend-models';

export enum UwContext {
  NATURAL_HAZARDS = 'NATURAL_HAZARDS',
  OUTBUILDING = 'OUTBUILDING',
}

export type UwContextKey = UwContext | (string & Record<never, never>);

export interface UwSharedDecision {
  measures: UnderwriterMeasure[];
  documentation: string;
  underwriterName: string;
  underwriterBensl: string;
  risikoAddress: string;
  produktName: string;
  timestamp?: string;
}

export interface UwContextEntry {
  contextKey: UwContextKey;
  isRequired: boolean;
  willUnderwrite: boolean | null;
  isAcceptedByAgent?: boolean;
  files: UploadedFile[];
  texts: Record<string, string>;
  reason: string;
}

export interface ProductUw {
  shared: UwSharedDecision;
  entries: UwContextEntry[];
}

export interface UwProductEntry {
  appId: App;
  angebotsId: string;
  uw: ProductUw;
}

export interface UnderwriterFragebogenCheckout {
  disableSubmission: boolean;
  products: UwProductEntry[];
}

export interface UwEvalContext {
  featureToggles: FeatureToggles;
  selectedZusatzoptionen: Array<{ id: string }>;
  isNeugeschaeft: boolean;
}

export enum UwSubmissionField {
  NATURAL_HAZARDS = 'underwriterDecisionAntrag',
  OUTBUILDING = 'nebengebaedeUnderwriterDecisionAntrag',
}

export enum UwPrintField {
  NATURAL_HAZARDS = 'underwriterDecisionDruck',
  OUTBUILDING = 'nebengebaeudeUnderwriterDecisionDruck',
}

export interface UwWireBinding {
  submissionField: UwSubmissionField;
  printField: UwPrintField;
}

export interface UwTextFieldDescriptor {
  id: string;
  labelKey: string;
  required: boolean;
  maxLength?: number;
  pattern?: string;
}

export interface UwCardCopy {
  requiredForTitle: string;
  uploadSectionTitle: string;
  uploadSectionHint?: string;
  uploadRequiredErrorKey: string;
  uploadRequiredErrorDataCy: string;
  readonlyTitle: string;
  readonlyFieldLabels: Record<string, string>;
  requiredFieldPlaceholder: Record<string, string>;
  requiredFieldErrorKey: Record<string, string>;
  templateDownload?: { hrefKey: string; labelKey: string };
  downloadErrorDataCyPrefix?: string;
}

export interface UwReadonlyCardPolicy {
  showFilesTitle?: boolean;
  hideWithoutFiles?: boolean;
}

export interface UwFragebogenFieldDataCy {
  textarea?: string;
  errorRequired?: string;
  errorLength: string;
  errorPattern: string;
}

export interface UwContextDescriptor<TProdukt = unknown> {
  key: UwContextKey;
  ordinal: number;
  requiredWhen?: (p: TProdukt, s: UwEvalContext) => boolean;
  isBlockingRisk?: (p: TProdukt) => boolean;
  enabledWhen?: (t: FeatureToggles) => boolean;
  appliesTo?: (spartenType: SpartenType) => boolean;
  buildReason: (p: TProdukt) => string;
  uploader: FileUploader;
  textFields: UwTextFieldDescriptor[];
  i18n: { title: string; reason: string; rejectionMeasure?: string };
  wire?: UwWireBinding;
  copy?: Partial<UwCardCopy>;
  readonlyCard?: UwReadonlyCardPolicy;
  fieldDataCy?: Record<string, UwFragebogenFieldDataCy>;
}
